import {
  useCurrentLanguage,
  useGetFindingsQuery,
  usePackageId,
  useProjectId,
  useServiceNameOrNull,
} from '@consigli/hooks';
import { ConflictType, type Finding, ServiceName } from '@consigli/types';
import { uniqueBy } from '@consigli/utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import {
  rfpFindingsQuery,
  type RfpFindingsConflictType,
} from '@/pages/insight/dashboard/rfp/rfp-findings-query';

import { type CitedFinding } from './sources';

const RFP_CONFLICT_TYPES = new Set<ConflictType>([
  ConflictType.MILESTONE,
  ConflictType.DELIVERABLE,
]);

/**
 * The findings an answer cited, in the order it cited them.
 *
 * The findings endpoint takes no list of ids, so each request is a whole list,
 * narrowed here to what was cited. No status filter either: a finding dismissed
 * after it was cited still grounded the answer.
 *
 * The package-wide list leaves milestones and response requirements out on an
 * RFP package, so those come from the RFP dashboard's own requests, which RTK
 * Query has usually cached already. A finding the stream sent without its
 * conflict type could be any of the three, so on an RFP package it asks all
 * three lists. A list that no citation can be in is never requested.
 */
export const useCitedFindings = (
  cited: CitedFinding[],
): { findings: Finding[]; isLoading: boolean } => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const language = useCurrentLanguage();
  const isRfpPackage = useServiceNameOrNull() === ServiceName.RFP_RFQ_REVIEW;

  const conflictTypes = cited.map((finding) => finding.conflictType);
  const hasUntyped = conflictTypes.includes(undefined);
  const needsPackageList = conflictTypes.some(
    (conflictType) => conflictType === undefined || !RFP_CONFLICT_TYPES.has(conflictType),
  );
  const needsRfpList = (conflictType: RfpFindingsConflictType) =>
    conflictTypes.includes(conflictType) || (hasUntyped && isRfpPackage);

  const packageList = useGetFindingsQuery(
    needsPackageList ? { projectId, packageId, page: 'all', language } : skipToken,
  );
  const milestones = useGetFindingsQuery(
    needsRfpList(ConflictType.MILESTONE)
      ? rfpFindingsQuery(projectId, packageId, ConflictType.MILESTONE)
      : skipToken,
  );
  const deliverables = useGetFindingsQuery(
    needsRfpList(ConflictType.DELIVERABLE)
      ? rfpFindingsQuery(projectId, packageId, ConflictType.DELIVERABLE)
      : skipToken,
  );

  const findings = useMemo(() => {
    const findingsById = new Map<number, Finding>();
    for (const list of [packageList.data, milestones.data, deliverables.data]) {
      for (const finding of list?.results ?? []) findingsById.set(finding.id, finding);
    }
    return uniqueBy(cited, (finding) => finding.findingId).flatMap(({ findingId }) => {
      const finding = findingsById.get(findingId);
      return finding ? [finding] : [];
    });
  }, [cited, packageList.data, milestones.data, deliverables.data]);

  return {
    findings,
    isLoading: packageList.isLoading || milestones.isLoading || deliverables.isLoading,
  };
};
